import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';

/**
 * Helper interno: registra un evento no-CRUD en audit_log via RPC.
 *
 * **Best-effort, silent-fail por diseño.** Casos donde puede fallar:
 *   - La migration `20260526000002_audit_log_triggers` no se aplicó
 *     todavía en la BD local → la RPC `log_audit_event` no existe.
 *   - El usuario no tiene permiso para ejecutar la RPC.
 *   - Problema de red puntual.
 *
 * En NINGUNO de esos casos queremos romper el flujo de autenticación.
 * El logging es valioso pero no crítico — si falla, registramos en
 * console para debug y seguimos.
 */
async function logAuditEvent(accion: string, datos?: Record<string, unknown>) {
  try {
    const { error } = await supabase.rpc('log_audit_event', {
      p_accion: accion,
      p_datos: datos ?? null,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(
        `[audit] No se pudo registrar evento "${accion}": ${error.message}. ` +
          `¿Está aplicada la migration 20260526000002_audit_log_triggers?`,
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[audit] Error inesperado registrando "${accion}":`, e);
  }
}

// Usuario cuyo LOGIN ya se registró en esta pestaña (sessionStorage: sobrevive a
// recargas, se pierde al cerrar la pestaña). Evita auditar el mismo login muchas veces.
const LOGIN_AUDIT_KEY = 'atsa.login_audit_uid';

function getLoginAuditado(): string | null {
  try {
    return sessionStorage.getItem(LOGIN_AUDIT_KEY);
  } catch {
    return null;
  }
}

function setLoginAuditado(userId: string | null) {
  try {
    if (userId) sessionStorage.setItem(LOGIN_AUDIT_KEY, userId);
    else sessionStorage.removeItem(LOGIN_AUDIT_KEY);
  } catch {
    // sessionStorage no disponible (modo privado, etc.): se registra igual
  }
}

export type UserRole = 'admin_general' | 'operador' | 'admin_empresa';

export interface UserProfile {
  id: string;
  /** CUIT/CUIL de la persona (11 dígitos). Identificador de login. */
  cuit: string | null;
  /** Correo de contacto (para recuperar clave). Ya NO es la identidad de login. */
  email: string;
  rol: UserRole;
  empresa_id: string | null;
  nombre: string;
  apellido: string | null;
  activo: boolean;
}

interface AuthContextValue {
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;

  // Impersonacion: el admin general puede ver como una empresa
  impersonatingEmpresaId: string | null;
  startImpersonation: (empresaId: string) => void;
  stopImpersonation: () => void;

  /**
   * Rol efectivo: el rol con el que el sistema debe comportarse en pantalla.
   * Cuando hay impersonacion activa, devuelve 'admin_empresa' independientemente
   * del rol real del usuario. Usar este valor para decisiones de UI (menu,
   * permisos visibles, filtros). Para decisiones de seguridad criticas (BD)
   * usar siempre el `profile.rol` real + RLS.
   */
  effectiveRole: UserRole | null;

  /**
   * Empresa efectiva: la empresa con la que se trabaja en pantalla.
   * - Si hay impersonacion: el empresaId impersonado
   * - Si el usuario es admin_empresa: su empresa_id
   * - En otro caso: null (admin/operador ven todas)
   */
  effectiveEmpresaId: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const IMPERSONATION_KEY = 'atsa.impersonating_empresa_id';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatingEmpresaId, setImpersonatingEmpresaId] = useState<string | null>(
    sessionStorage.getItem(IMPERSONATION_KEY)
  );

  useEffect(() => {
    let currentUserId: string | null = null;

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        currentUserId = data.session.user.id;
        loadProfile(data.session.user.id);
      } else {
        setLoading(false);
      }
    });

    // No recargar el perfil en TOKEN_REFRESHED (mismo usuario, solo nuevo token)
    const { data: listener } = supabase.auth.onAuthStateChange((evt, newSession) => {
      setSession(newSession);
      const newUserId = newSession?.user?.id ?? null;
      const userChanged = newUserId !== currentUserId;
      currentUserId = newUserId;

      if (newSession?.user) {
        // El perfil solo se recarga cuando entra otro usuario. supabase-js vuelve
        // a emitir SIGNED_IN cada vez que la pestaña recupera el foco; eso no es un
        // usuario nuevo y recargar el perfil en cada foco es tráfico inútil.
        if (userChanged) {
          loadProfile(newSession.user.id);
        }
        // Audit log de LOGIN — una sola vez por sesión de pestaña. Como SIGNED_IN se
        // repite al recuperar el foco o al recargar, se deduplica por usuario en
        // sessionStorage (antes llenaba audit_log de logins falsos).
        if (evt === 'SIGNED_IN' && getLoginAuditado() !== newSession.user.id) {
          setLoginAuditado(newSession.user.id);
          // El email de auth es sintético (<cuit>@atsa.interno); logueamos el CUIT.
          const cuit = newSession.user.email?.split('@')[0] ?? null;
          void logAuditEvent('LOGIN', { cuit });
        }
      } else {
        // Sesión terminada (logout, expiración): el próximo login vuelve a registrarse.
        setLoginAuditado(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  async function loadProfile(userId: string) {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('id', userId)
      .single();
    if (!error && data) setProfile(data as UserProfile);
    setLoading(false);
  }

  async function signOut() {
    // Audit log ANTES de cerrar sesión (después de signOut, auth.uid() es null
    // y el log quedaría sin usuario).
    await logAuditEvent('LOGOUT');
    stopImpersonation();
    await supabase.auth.signOut();
    setLoginAuditado(null);
    setProfile(null);
  }

  function startImpersonation(empresaId: string) {
    if (profile?.rol !== 'admin_general') return;
    sessionStorage.setItem(IMPERSONATION_KEY, empresaId);
    setImpersonatingEmpresaId(empresaId);
    void logAuditEvent('IMPERSONATE_START', { empresa_id: empresaId });
  }

  function stopImpersonation() {
    const wasImpersonating = sessionStorage.getItem(IMPERSONATION_KEY);
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonatingEmpresaId(null);
    if (wasImpersonating) {
      void logAuditEvent('IMPERSONATE_STOP', { empresa_id: wasImpersonating });
    }
  }

  // Calcular rol y empresa efectivos
  const effectiveRole: UserRole | null = useMemo(() => {
    if (!profile) return null;
    if (impersonatingEmpresaId) return 'admin_empresa';
    return profile.rol;
  }, [profile, impersonatingEmpresaId]);

  const effectiveEmpresaId: string | null = useMemo(() => {
    if (impersonatingEmpresaId) return impersonatingEmpresaId;
    if (profile?.rol === 'admin_empresa') return profile.empresa_id;
    return null;
  }, [profile, impersonatingEmpresaId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile,
      loading,
      signOut,
      impersonatingEmpresaId,
      startImpersonation,
      stopImpersonation,
      effectiveRole,
      effectiveEmpresaId,
    }),
    [session, profile, loading, impersonatingEmpresaId, effectiveRole, effectiveEmpresaId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
